import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Resizable } from 'react-resizable';
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import PDFObject from 'pdfobject';
import { getTracking } from 'ts-tracking-number';

import B3Spin from '@/components/spin/B3Spin';
import { getInvoicePdf } from '@/shared/service/genesee/invoice'

interface RowList {
  [key: string]: CustomFieldItems | string | number;
  id: string;
  createdAt: number;
  updatedAt: number;
  PDF: string;
  TrackingNumbers: string[];
}

const templateMinHeight = 300;

interface PrintTemplateProps {
  row: RowList;
}

function PrintTemplate({ row }: PrintTemplateProps) {
  const container = useRef<HTMLInputElement | null>(null);

  const dom = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState<boolean>(false);

  const [height, setHeight] = useState<number>(templateMinHeight);

  const onFirstBoxResize = (
    _: SyntheticEvent<Element, Event>,
    { size }: { size: { height: number } },
  ) => {
    setHeight(size.height);
  };

  useEffect(() => {
    const viewPrint = async () => {
      setLoading(true);
      const invoicePDFUrl = row.PDF;

      if (!invoicePDFUrl) {
        setLoading(false);
        return;
      }

      const blob = await getInvoicePdf(invoicePDFUrl)
      const pdfBlobUrl = URL.createObjectURL(blob);

      if (!container?.current) return;

      PDFObject.embed(pdfBlobUrl, container.current);

      setLoading(false);
    };

    viewPrint();

    return () => {
      container.current = null;
    };
  }, [row]);

  const getTrackingInfo = (trackingNumber: string) => {
    const tracking = getTracking(trackingNumber);

    return tracking;
  };

  return (
    <B3Spin isSpinning={loading}>
      <Box
        ref={dom}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          width: '100%',
          '& .box': {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            '& .react-resizable': {
              position: 'relative',
            },
            '& .react-resizable-handle': {
              position: 'absolute',
              width: '100%',
              height: '30px',
              backgroundRepeat: 'no-repeat',
              backgroundOrigin: 'content-box',
              boxSizing: 'border-box',
            },
            '& .react-resizable-handle-s': {
              cursor: 'ns-resize',
              bottom: 0,
            },
          },
        }}
      >
        {row.TrackingNumbers.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell>Tracking Numbers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {row.TrackingNumbers.map((number: string, index: number) => (
                <TableRow key={index}>
                  <TableCell>
                    <Link
                      style={{
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        // @ts-ignore
                        const urlTemplate = getTrackingInfo(number).trackingUrl;
                        // @ts-ignore
                        const url = urlTemplate.replace(/%s/g, number);
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      {number}
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
        {row.PDF && (
          <Resizable
            className="box"
            height={height}
            minConstraints={[dom?.current?.offsetWidth || 0, 0]}
            width={dom.current?.offsetWidth || 0}
            onResize={onFirstBoxResize}
            resizeHandles={['s']}
          >
            <div style={{ width: '100%', height: `${height}px` }}>
              <div ref={container} style={{ height: '100%', width: '100%' }} />
            </div>
          </Resizable>
        )}
      </Box>
    </B3Spin>
  );
}

export default PrintTemplate;
